import React from 'react';
import { useTranslation } from 'react-i18next';
import { Input } from 'components/ui/input';
import { Checkbox } from 'components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from 'components/ui/RadioGroup';
import { cn } from 'lib/utils';
import { QuizSpec } from 'modules/learnArtifacts';
type QuizArtifactEditorProps = {
    value: QuizSpec;
    onChange: (next: QuizSpec) => void;
};
export const QuizArtifactEditor: React.FC<QuizArtifactEditorProps> = ({ value, onChange }) => {
    const { t } = useTranslation();
    const updateQuestion = (index: number, updates: Partial<QuizSpec['questions'][number]>) => {
        onChange({
            ...value,
            questions: value.questions.map((q, i) => (i === index ? { ...q, ...updates } : q)),
        });
    };
    const toggleCorrect = (questionIndex: number, optionIndex: number, type: 'single_choice' | 'multiple_choice') => {
        const question = value.questions[questionIndex];
        let nextCorrect: number[];
        if (type === 'single_choice') {
            nextCorrect = [optionIndex];
        }
        else {
            if (question.correctIndexes.includes(optionIndex)) {
                nextCorrect = question.correctIndexes.filter((i) => i !== optionIndex);
                if (!nextCorrect.length)
                    return; // must have at least 1 correct
            }
            else {
                nextCorrect = [...question.correctIndexes, optionIndex];
            }
        }
        updateQuestion(questionIndex, { correctIndexes: nextCorrect });
    };
    const updateOption = (questionIndex: number, optionIndex: number, text: string) => {
        const options = [...value.questions[questionIndex].options];
        options[optionIndex] = text;
        updateQuestion(questionIndex, { options });
    };
    return (<div className="space-y-4">
      <div>
        <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">{t("draft_title")}</label>
        <Input value={value.title} onChange={(e) => onChange({ ...value, title: e.target.value })} placeholder={t("assessment_title")}/>
      </div>

      <div className="space-y-4">
        {value.questions.map((question, index) => (<div key={question.id} className="rounded-lg border border-border bg-card p-4">
            {/* Header row */}
            <div className="mb-3 flex items-center justify-between gap-2">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">{t("question")} {index + 1}
              </p>
              {/* Question type is fixed for AI-generated drafts — shown for
                  reference only, not switchable. */}
              <span className="rounded-md bg-muted px-2.5 py-0.5 text-xs font-medium text-muted-foreground">
                {question.type === 'single_choice' ? t("single_choice") : t("multiple_choice")}
              </span>
            </div>

            {/* Question text */}
            <label className="mb-1 block text-xs font-medium text-muted-foreground">{t("question")}</label>
            <Input value={question.question} onChange={(e) => updateQuestion(index, { question: e.target.value })} placeholder={t("enter_question")}/>

            {/* Options */}
            <p className="mb-2 mt-3 text-xs font-medium text-muted-foreground">{t("options_u2014")}{' '}
              <span className="text-muted-foreground/70">
                {question.type === 'single_choice'
                ? t("click_circle_to_mark_correct_answer")
                : t("click_squares_to_mark_all_correct_answers")}
              </span>
            </p>
            {question.type === 'single_choice' ? (
              <RadioGroup
                value={String(question.correctIndexes[0] ?? '')}
                onValueChange={(v) =>
                  updateQuestion(index, { correctIndexes: [Number(v)] })
                }
                className="space-y-2"
              >
                {question.options.map((option, optIdx) => {
                  const isCorrect = question.correctIndexes.includes(optIdx);
                  const itemId = `quiz-${question.id}-opt-${optIdx}`;
                  return (
                    <div key={optIdx} className="flex items-center gap-2">
                      <RadioGroupItem
                        id={itemId}
                        value={String(optIdx)}
                        aria-label={isCorrect ? t('marked_correct') : t('mark_as_correct')}
                      />
                      <Input
                        value={option}
                        onChange={(e) => updateOption(index, optIdx, e.target.value)}
                        placeholder={t('option_index', { index: optIdx + 1 })}
                        className={cn(
                          isCorrect &&
                            'border-emerald-200 bg-emerald-50/50 dark:border-emerald-800/60 dark:bg-emerald-950/20',
                        )}
                      />
                    </div>
                  );
                })}
              </RadioGroup>
            ) : (
              <div className="space-y-2">
                {question.options.map((option, optIdx) => {
                  const isCorrect = question.correctIndexes.includes(optIdx);
                  return (
                    <div key={optIdx} className="flex items-center gap-2">
                      <Checkbox
                        checked={isCorrect}
                        onCheckedChange={() => toggleCorrect(index, optIdx, question.type)}
                        aria-label={isCorrect ? t('marked_correct') : t('mark_as_correct')}
                      />
                      <Input
                        value={option}
                        onChange={(e) => updateOption(index, optIdx, e.target.value)}
                        placeholder={t('option_index', { index: optIdx + 1 })}
                        className={cn(
                          isCorrect &&
                            'border-emerald-200 bg-emerald-50/50 dark:border-emerald-800/60 dark:bg-emerald-950/20',
                        )}
                      />
                    </div>
                  );
                })}
              </div>
            )}
          </div>))}
      </div>
    </div>);
};
export default QuizArtifactEditor;
